// brazilV2Copy.ts
// ─── Astria Brazil v2 — bilingual (EN/PT) UI copy ──────────────────────────
// All fixed UI strings for the shell header, tab bar, and the tab screen's
// chrome (loading/reset/submit/CTA text). The client-approved backend copy
// pack response text itself is NOT translated here — it comes back in
// Brazilian Portuguese from helper/astriaBrazilV2Service.js by default, or
// in English when `wizard.lang === "en"` (see that file's EN_COPY_PACK /
// BR_COPY_PACK). This file only localizes the surrounding UI chrome, and
// its `lang` toggle is the same value sent to the backend as
// `wizard.lang` — see BrazilV2Tab.tsx.
//
// Tab bar labels use the client's BR v2 UI JSON nav names where available
// (Caminho Claro, Clima Emocional, Retrato Interior, Fluxo do Tempo, Sua
// Nota); "Equilíbrio Emocional" fills the one data tab (emotion_balance)
// that has no direct nav label in the client's 5-tab nav array.
import { BrazilV2Lang } from "./brazilV2Language";
import { AstriaBrazilV2Tab } from "./AstriaBrazilV2Shell";

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface TabBarCopy {
  label: string;
  sublabel: string;
}

interface TabScreenCopy {
  question: string;
  helper: string;
  fields: Record<string, { label: string; options?: Record<string, string>; placeholder?: string; lowLabel?: string; highLabel?: string }>;
}

interface BrazilV2CopyShape {
  shell: ShellCopy;
  tabBar: Record<AstriaBrazilV2Tab, TabBarCopy>;
  screens: Record<AstriaBrazilV2Tab, TabScreenCopy>;
  submitButton: string;
  loadingLabel: string;
  resetLink: string;
}

export const BRAZIL_V2_COPY: Record<BrazilV2Lang, BrazilV2CopyShape> = {
  pt: {
    shell: {
      title: "Astria Brasil",
      subtitle: "Um espaço leve e acolhedor pra você se reconectar com o que sente",
      langToggleLabel: "Idioma",
    },
    tabBar: {
      soft_summary: { label: "Retrato Interior", sublabel: "Retrato Interior" },
      connection_atmosphere: { label: "Clima Emocional", sublabel: "Clima Emocional" },
      timing_rhythm: { label: "Fluxo do Tempo", sublabel: "Fluxo do Tempo" },
      emotion_balance: { label: "Equilíbrio Emocional", sublabel: "Equilíbrio Emocional" },
      guidance_path: { label: "Caminho Claro", sublabel: "Caminho Claro" },
      your_note: { label: "Sua Nota", sublabel: "Sua Nota" },
    },
    screens: {
      soft_summary: {
        question: "Como você tá se sentindo agora?",
        helper: "Um momento leve pra você olhar pro que sente agora.",
        fields: {
          mood: { label: "Sentimento" },
          intensity: {
            label: "Intensidade desse sentimento",
            lowLabel: "Leve",
            highLabel: "Forte",
          },
        },
      },
      connection_atmosphere: {
        question: "Como tá o clima entre vocês dois?",
        helper: "O calor (ou a distância) que você sente com essa pessoa.",
        fields: {
          connection: {
            label: "Clima entre vocês",
            options: {
              warm: "Caloroso",
              distant: "Distante",
              tense: "Tenso",
              playful: "Leve e divertido",
              quiet: "Quieto",
            },
          },
          tone: {
            label: "O tom da pessoa",
            options: {
              gentle: "Gentil",
              guarded: "Reservado",
              open: "Aberto",
              unsure: "Incerto",
            },
          },
        },
      },
      timing_rhythm: {
        question: "Qual é o ritmo desse momento?",
        helper: "O passo e a prontidão desse momento.",
        fields: {
          flow: {
            label: "Ritmo atual",
            options: {
              slow: "Devagar",
              steady: "Estável",
              rushed: "Apressado",
              uncertain: "Incerto",
            },
          },
          readiness: {
            label: "O quanto você se sente pronto",
            lowLabel: "Ainda não",
            highLabel: "Pronto",
          },
        },
      },
      emotion_balance: {
        question: "Onde suas emoções estão agora?",
        helper: "Onde suas emoções estão se acomodando agora.",
        fields: {
          emotion_zone: {
            label: "Zona emocional",
            options: {
              warm: "Quente",
              heavy: "Pesada",
              light: "Leve",
              tender: "Sensível",
              steady: "Estável",
            },
          },
          location: {
            label: "Onde você mais sente isso?",
            options: {
              chest: "Peito",
              mind: "Mente",
              stomach: "Estômago",
              everywhere: "No corpo todo",
            },
          },
        },
      },
      guidance_path: {
        question: "O que tá acontecendo?",
        helper: "Um próximo passo leve pro que você tá enfrentando.",
        fields: {
          issue: {
            label: "O que tá acontecendo",
            options: {
              communication: "Comunicação",
              distance: "Distância",
              uncertainty: "Incerteza",
              overwhelm: "Sobrecarga",
            },
          },
          desired: {
            label: "O que você gostaria de sentir?",
            options: {
              clarity: "Clareza",
              closeness: "Proximidade",
              peace: "Paz",
              courage: "Coragem",
            },
          },
        },
      },
      your_note: {
        question: "O que tá passando pela sua cabeça?",
        helper: "Uma resposta leve pra sua última mensagem.",
        fields: {
          latest_message: {
            label: "Sua última mensagem",
            placeholder: "O que você gostaria de dizer?",
          },
          feeling: {
            label: "Como você se sentiu escrevendo isso?",
            options: {
              soft: "Leve",
              hopeful: "Esperançoso",
              unsure: "Incerto",
              relieved: "Aliviado",
            },
          },
        },
      },
    },
    submitButton: "Ver resultado",
    loadingLabel: "Lendo o que você tá sentindo…",
    resetLink: "Começar um novo check-in",
  },
  en: {
    shell: {
      title: "Astria Brazil",
      subtitle: "A warm, light space to check in with how you're feeling",
      langToggleLabel: "Language",
    },
    tabBar: {
      soft_summary: { label: "Soft Summary", sublabel: "Retrato Interior" },
      connection_atmosphere: { label: "Connection Atmosphere", sublabel: "Clima Emocional" },
      timing_rhythm: { label: "Timing Rhythm", sublabel: "Fluxo do Tempo" },
      emotion_balance: { label: "Emotion Balance", sublabel: "Equilíbrio Emocional" },
      guidance_path: { label: "Guidance Path", sublabel: "Caminho Claro" },
      your_note: { label: "Your Note", sublabel: "Sua Nota" },
    },
    screens: {
      soft_summary: {
        question: "How are you feeling right now?",
        helper: "A light check-in on how you're feeling right now.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "How strong is that feeling?",
            lowLabel: "Light",
            highLabel: "Strong",
          },
        },
      },
      connection_atmosphere: {
        question: "What's the atmosphere between you two?",
        helper: "The warmth (or distance) you're feeling with someone else.",
        fields: {
          connection: {
            label: "Atmosphere between you two",
            options: {
              warm: "Warm",
              distant: "Distant",
              tense: "Tense",
              playful: "Playful",
              quiet: "Quiet",
            },
          },
          tone: {
            label: "Their tone",
            options: {
              gentle: "Gentle",
              guarded: "Guarded",
              open: "Open",
              unsure: "Unsure",
            },
          },
        },
      },
      timing_rhythm: {
        question: "What's the rhythm of this moment?",
        helper: "The pace and readiness of this moment.",
        fields: {
          flow: {
            label: "Current flow",
            options: {
              slow: "Slow",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Uncertain",
            },
          },
          readiness: {
            label: "How ready do you feel?",
            lowLabel: "Not yet",
            highLabel: "Ready",
          },
        },
      },
      emotion_balance: {
        question: "Where are your emotions sitting?",
        helper: "Where your emotions are sitting right now.",
        fields: {
          emotion_zone: {
            label: "Emotion zone",
            options: {
              warm: "Warm",
              heavy: "Heavy",
              light: "Light",
              tender: "Tender",
              steady: "Steady",
            },
          },
          location: {
            label: "Where does it show up most?",
            options: {
              chest: "Chest",
              mind: "Mind",
              stomach: "Stomach",
              everywhere: "Everywhere",
            },
          },
        },
      },
      guidance_path: {
        question: "What's going on?",
        helper: "A soft next step for what you're facing.",
        fields: {
          issue: {
            label: "What's going on",
            options: {
              communication: "Communication",
              distance: "Distance",
              uncertainty: "Uncertainty",
              overwhelm: "Overwhelm",
            },
          },
          desired: {
            label: "What do you want?",
            options: {
              clarity: "Clarity",
              closeness: "Closeness",
              peace: "Peace",
              courage: "Courage",
            },
          },
        },
      },
      your_note: {
        question: "What's on your mind?",
        helper: "A gentle reflection on your latest message.",
        fields: {
          latest_message: {
            label: "Your latest message",
            placeholder: "What would you like to say?",
          },
          feeling: {
            label: "How did you feel while writing it?",
            options: {
              soft: "Soft",
              hopeful: "Hopeful",
              unsure: "Unsure",
              relieved: "Relieved",
            },
          },
        },
      },
    },
    submitButton: "Reveal",
    loadingLabel: "Reading your check-in…",
    resetLink: "Start a new check-in",
  },
};
